import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as ClientApi from '~/lib/ClientApi';
import { getBusinessId } from '~/lib/Api';
import { useEffect } from 'react';
import { registerCustomQuestions } from '~/lib/CustomQuestions/CustomQuestionRegistry';

/**
 * See the note on the admin copy in ~/lib/Queries: a persisted cache hit never
 *  runs the query function, so the registry is filled from the data instead.
 */
function useRegisterCustomQuestions(customQuestions) {
    useEffect(() => {
        registerCustomQuestions(customQuestions);
    }, [customQuestions]);
}

let queryClient = null;
export function setPublicQueryClient(qc) {
    queryClient = qc;
}

export function clearPublicQueries() {
    queryClient?.clear();
}

export function invalidatePublicQueries() {
    queryClient?.invalidateQueries();
}

export function useFormDetailsQuery(businessId, formId) {
    const url = new URL(document.location.href);
    const params = url.searchParams.toString();

    const query = useQuery({
        queryKey: [
            'form',
            parseInt(businessId),
            parseInt(formId),
            JSON.stringify(params),
        ],
        queryFn: async () => {
            const data = await axios
                .get(
                    `/api/forms/${formId}?params=${encodeURIComponent(params)}`,
                )
                .then(res => res.data);
            return {
                ...data,
                formSections: (data?.sections || []).filter(
                    s => !s.section.is_special,
                ),
            };
        },
        staleTime: 500,
    });

    useRegisterCustomQuestions(query.data?.customQuestions);

    return query;
}

export function useSubmissionQuery(token) {
    const query = useQuery({
        queryKey: ['submission', token],
        queryFn: async () =>
            axios.get(`/api/submission/${token}`).then(res => res.data),
    });

    useRegisterCustomQuestions(query.data?.customQuestions);

    return query;
}

export function useGiftCertificateQuery(token) {
    return useQuery({
        queryKey: ['gift-certificate-details', token],
        queryFn: () =>
            axios.get(`/api/gift-certificate/${token}`).then(res => res.data),
    });
}

export function useInvoiceQuery(token) {
    const url = new URL(document.location.href);
    const preview = url.searchParams.get('preview');

    return useQuery({
        queryKey: ['invoice-details', token, preview],
        queryFn: () => {
            const params = new URLSearchParams();
            if (preview) {
                params.append('preview', preview);
            }
            const queryString = params.toString();
            const apiUrl = `/api/invoice/${token}${queryString ? `?${queryString}` : ''}`;
            return axios.get(apiUrl).then(res => res.data);
        },
    });
}

export function useClientHistoryQuery() {
    return useQuery({
        queryKey: ['client-history', ClientApi.getToken()],
        queryFn: async () => {
            if (!ClientApi.getToken()) {
                return {};
            }
            return await ClientApi.get(`/account/history`);
        },
    });
}

export function setClientHistoryQuery(data) {
    queryClient.setQueryData(
        ['client-history', ClientApi.getToken()],
        old => data,
    );
}
